import React, { useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import toast from 'react-hot-toast'
import { submitVolunteerApplication } from '@/lib/api/public'

const schema = z.object({
  volunteer_type: z.enum(['individual', 'group']),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  email: z.string().email('Please enter a valid email'),
  phone: z.string().min(7, 'Phone number is required'),
  county: z.string().min(1, 'County is required'),
  other_county: z.string().optional(),
  group_name: z.string().optional(),
  group_leader: z.string().optional(),
  group_contact_email: z.string().email('Please enter a valid group contact email').optional().or(z.literal('')),
  group_size: z.preprocess(
    (v) => v === '' || v == null ? undefined : Number(v),
    z.number().int().min(1).max(500).optional()
  ),
  activity_date: z.string().optional(),
  activity_purpose: z.string().optional(),
  availability: z.string().min(1, 'Please tell us when you are available'),
  interests: z.array(z.string()).min(1, 'Select at least one volunteer interest'),
  notes: z.string().optional(),
  consent: z.boolean().refine(v => v, 'Please confirm the volunteer information is accurate'),
}).superRefine((v, ctx) => {
  if (v.volunteer_type === 'group' && !v.group_name?.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['group_name'], message: 'Group/organization name is required' })
  }
  if (v.county === 'Other' && !v.other_county?.trim()) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['other_county'], message: 'Please specify your county' })
  }
})

type FormData = z.infer<typeof schema>
type VolunteerType = FormData['volunteer_type']

const INTERESTS = [
  'Gift-box assembly',
  'Community outreach',
  'Flyer/resource distribution',
  'Diaper sorting & packing',
  'Donation sorting',
  'Event/workshop support',
  'Administrative support',
  'Skilled/professional support',
  'Other',
]

const COUNTIES = ['Bay','Calhoun','Franklin','Gadsden','Gulf','Holmes','Jackson','Jefferson','Leon','Liberty','Madison','Taylor','Wakulla','Washington','Other']

const Field: React.FC<{label:string; error?:string; children:React.ReactNode}> = ({label,error,children}) => (
  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-1">{label}</label>
    {children}
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
)

const inputClass = 'w-full rounded-xl border border-gray-300 px-4 py-3 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-pink-400'

interface VolunteerRegistrationFormProps {
  initialType?: VolunteerType
  onSuccess?: () => void
}

export default function VolunteerRegistrationForm({ initialType = 'individual', onSuccess }: VolunteerRegistrationFormProps) {
  const [submitted, setSubmitted] = React.useState(false)
  const { register, handleSubmit, watch, reset, formState:{errors,isSubmitting} } = useForm<FormData>({
    resolver: zodResolver(schema),
    shouldUnregister: true,
    defaultValues: { volunteer_type: initialType, interests:[], consent:false },
  })

  const type = watch('volunteer_type')

  const onInvalid = useCallback((formErrors: typeof errors) => {
    const first = Object.values(formErrors).find((e: any) => e?.message)?.message as string | undefined
    toast.error(first || 'Please review the required fields before submitting.')
  }, [])

  const onSubmit = useCallback(async (data: FormData) => {
    try {
      const interestText = data.interests.join(', ')
      const notes = [
        'PUBLIC VOLUNTEER REGISTRATION',
        `Volunteer type: ${data.volunteer_type}`,
        data.county === 'Other' && data.other_county?.trim() ? `County specified: ${data.other_county.trim()}` : null,
        data.group_name ? `Group/organization: ${data.group_name}` : null,
        data.group_leader ? `Group point of contact: ${data.group_leader}` : null,
        data.group_contact_email ? `Group point of contact email: ${data.group_contact_email}` : null,
        data.group_size ? `Estimated group size: ${data.group_size}` : null,
        data.activity_date ? `Activity date: ${data.activity_date}` : null,
        data.activity_purpose ? `Activity purpose: ${data.activity_purpose}` : null,
        `Volunteer interests: ${interestText}`,
        data.notes?.trim() ? `Additional notes: ${data.notes.trim()}` : null,
        'Registration confirmation: Yes',
      ].filter(Boolean).join('\n')

      await submitVolunteerApplication({
        first_name:data.first_name,
        last_name:data.last_name,
        email:data.email,
        phone:data.phone,
        county:data.county === 'Other' ? data.other_county : data.county,
        availability:data.availability,
        interests:data.interests,
        skills:'',
        notes,
        volunteer_type:data.volunteer_type,
        group_name:data.group_name,
        group_leader:data.group_leader,
        group_contact_email:data.group_contact_email,
        group_size:data.group_size,
        activity_date:data.activity_date,
        activity_purpose:data.activity_purpose,
      })

      setSubmitted(true)
      toast.success('Volunteer registration received!')
      reset({ volunteer_type: initialType, interests:[], consent:false })
      onSuccess?.()
    } catch (err) {
      console.error('[W.E.E. volunteer registration]', err)
      toast.error('We could not submit the registration. Please try again or email WEE.')
    }
  }, [initialType, onSuccess, reset])

  if (submitted) {
    return (
      <div className="rounded-3xl bg-white border border-pink-100 shadow-soft p-7 sm:p-9">
        <div className="text-center">
          <div className="text-5xl mb-3" aria-hidden="true">💛</div>
          <p className="text-sm font-semibold uppercase tracking-wider text-pink-600">W.E.E. Guided Access</p>
          <h2 className="text-2xl font-bold text-gray-900 mt-2">Volunteer registration received!</h2>
          <p className="text-gray-600 mt-3 leading-relaxed">Thank you for volunteering with Women Empowering Each Other. W.E.E. has received your registration and can use it to coordinate volunteer opportunities and activities.</p>
          <div className="mt-5 rounded-2xl bg-pink-50 border border-pink-100 p-4 text-sm text-gray-700 text-left">
            If you are registering a group, please have each participating volunteer complete their own registration when practical so W.E.E. can maintain accurate individual volunteer records.
          </div>
          <button
            type="button"
            onClick={() => { setSubmitted(false); reset({ volunteer_type: initialType, interests:[], consent:false }) }}
            className="mt-5 rounded-2xl px-6 py-3 font-bold text-white bg-gradient-to-r from-pink-600 to-pink-800"
          >
            Register another volunteer
          </button>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-5" noValidate>
      <Field label="Volunteer type" error={errors.volunteer_type?.message}>
        <div className="grid sm:grid-cols-2 gap-3">
          {(['individual','group'] as const).map(v => (
            <label key={v} className={`rounded-2xl border-2 p-4 cursor-pointer transition-colors ${type===v?'border-pink-500 bg-pink-50':'border-gray-200 hover:border-pink-200'}`}>
              <input type="radio" value={v} {...register('volunteer_type')} className="mr-2" />
              <span className="font-semibold">{v==='individual'?'Individual Volunteer':'Group Volunteer'}</span>
              <span className="block text-xs text-gray-500 mt-1">{v==='individual'?'Register yourself.':'Register with your organization or group.'}</span>
            </label>
          ))}
        </div>
      </Field>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="First name" error={errors.first_name?.message}><input className={inputClass} autoComplete="given-name" {...register('first_name')} /></Field>
        <Field label="Last name" error={errors.last_name?.message}><input className={inputClass} autoComplete="family-name" {...register('last_name')} /></Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Email" error={errors.email?.message}><input className={inputClass} type="email" autoComplete="email" {...register('email')} /></Field>
        <Field label="Phone" error={errors.phone?.message}><input className={inputClass} type="tel" autoComplete="tel" {...register('phone')} /></Field>
      </div>
      <Field label="County" error={errors.county?.message}>
        <select className={inputClass} {...register('county')}><option value="">Select county…</option>{COUNTIES.map(c=><option key={c}>{c}</option>)}</select>
      </Field>

      {watch('county') === 'Other' && (
        <Field label="Please specify your county" error={errors.other_county?.message}>
          <input className={inputClass} placeholder="Enter your county" {...register('other_county')} />
        </Field>
      )}

      {type==='group' && (
        <div className="grid sm:grid-cols-2 gap-4 rounded-2xl bg-orange-50 border border-orange-100 p-4">
          <Field label="Group / organization name" error={errors.group_name?.message}><input className={inputClass} {...register('group_name')} /></Field>
          <Field label="Group point of contact"><input className={inputClass} {...register('group_leader')} placeholder="Name of group leader/contact" /></Field>
          <Field label="Group point of contact email" error={errors.group_contact_email?.message}><input className={inputClass} type="email" autoComplete="email" placeholder="contact@organization.org" {...register('group_contact_email')} /></Field>
          <Field label="Estimated group size"><input className={inputClass} type="number" min="1" max="500" placeholder="e.g. 12" {...register('group_size')} /></Field>
          <Field label="Planned activity date"><input className={inputClass} type="date" {...register('activity_date')} /></Field>
          <div className="sm:col-span-2"><Field label="What will the group be helping with?"><input className={inputClass} {...register('activity_purpose')} placeholder="Example: gift-box assembly and W.E.E. resource outreach" /></Field></div>
        </div>
      )}

      <Field label="When are you generally available?" error={errors.availability?.message}>
        <textarea className={inputClass} rows={3} placeholder="Example: Saturdays, 10 AM–2 PM; occasional weekday events" {...register('availability')} />
      </Field>

      <Field label="How would you like to help?" error={errors.interests?.message}>
        <div className="grid sm:grid-cols-2 gap-2">
          {INTERESTS.map(item => (
            <label key={item} className="flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2.5 text-sm hover:bg-pink-50">
              <input type="checkbox" value={item} {...register('interests')} />
              {item}
            </label>
          ))}
        </div>
      </Field>

      <Field label="Anything else W.E.E. should know?">
        <textarea className={inputClass} rows={3} placeholder="Skills, accessibility needs, group details, or other information" {...register('notes')} />
      </Field>

      <label className="flex items-start gap-3 rounded-2xl bg-gray-50 border border-gray-200 p-4 text-sm text-gray-700">
        <input type="checkbox" className="mt-1" {...register('consent')} />
        <span>I confirm that the information above is accurate and I consent to W.E.E. contacting me about volunteer opportunities and coordination.</span>
      </label>
      {errors.consent && <p className="text-xs text-red-600">{errors.consent.message}</p>}

      <button type="submit" disabled={isSubmitting} className="w-full rounded-2xl px-6 py-4 font-bold text-white bg-gradient-to-r from-pink-600 to-pink-800 disabled:opacity-60">
        {isSubmitting ? 'Submitting registration…' : 'Submit Volunteer Registration'}
      </button>
      <p className="text-xs text-center text-gray-500">W.E.E. Guided Access™ · Women Empowering Each Other, Inc. · 501(c)(3)</p>
    </form>
  )
}
